import { useState } from 'react';
import type { ReactNode, VoidFunctionComponent } from 'react';

import { isFreeTier } from '../../../../../shared/subscriptions';
import { window } from '../../../../globals';
import { ErrorAlert } from '../../../generic/ErrorAlert';
import PaidFeatureGate from '../../../generic/Gates/PaidFeatureGate';
import Loader from '../../../generic/Loader';
import OrganizationSettingsPermissionGate from '../../OrganizationSettingsPermissionGate';
import { shouldDisplayJiraSettings } from '../../shared/permissions';

import JiraSettingsComponents from './JiraSettingsComponents';
import JiraSettingsConfiguration from './JiraSettingsConfiguration';
import JiraSettingsCredentials from './JiraSettingsCredentials';
import JiraSettingsCustomFields from './JiraSettingsCustomFields';
import JiraSettingsEmpty from './JiraSettingsEmpty';
import JiraSettingsHeader from './JiraSettingsHeader';
import JiraSettingsHeadersTable from './JiraSettingsHeadersTable';
import JiraSettingsSiteSelection from './JiraSettingsSiteSelection';
import JiraSettingsStatus from './JiraSettingsStatus';
import ModalAddJira from './ModalAddJira';
import useJiraSettings from './useJiraSettings';

type OnAddSiteFn = (site: string) => Promise<void>;

/**
 * The global Jira settings page.
 */
const JiraSettings: VoidFunctionComponent = () => {
  const jiraData = useJiraSettings();
  let body: ReactNode;

  const [shouldShowAddJiraModal, setShouldShowAddJiraModal] = useState(false);
  const onAddSiteFn: OnAddSiteFn = jiraData.onAddSite;
  const isLoading: boolean = jiraData.isLoading;

  const isOrgFreeTier = isFreeTier({
    plan: window.user?.organization?.stripe_plan,
    stripePriceLimit: window.user?.organization?.stripePriceLimit,
  });

  const onShowAddSiteModal = () => {
    setShouldShowAddJiraModal(true);
  };

  if (!jiraData.hasData) {
    body = (
      <PaidFeatureGate hidePill isRestricted={isOrgFreeTier}>
        <JiraSettingsEmpty
          onClickAddSite={onShowAddSiteModal}
        />
      </PaidFeatureGate>);
  } else {
    const {
      siteSelection,
      errors,
      configuration,
      credentials,
      status,
      customFields,
      headers,
      components,
      onSaveSite,
      isSaveButtonEnabled,
    } = jiraData;

    if (siteSelection.sites.length === 0) {
      body = (
        <PaidFeatureGate hidePill isRestricted={isOrgFreeTier}>
          <JiraSettingsEmpty
            onClickAddSite={onShowAddSiteModal}
          />
        </PaidFeatureGate>);
    } else {
      body = (
        /* eslint-disable react/jsx-props-no-spreading */
        <>
          <JiraSettingsHeader onSave={onSaveSite} isSaveButtonEnabled={isSaveButtonEnabled} />
          {errors && <ErrorAlert status={{ errors }} />}
          <JiraSettingsSiteSelection
            {...siteSelection}
            isPaidRestricted={isOrgFreeTier}
            onClickAddSite={onShowAddSiteModal}
          />
          <PaidFeatureGate hidePill isRestricted={isOrgFreeTier}>
            <JiraSettingsConfiguration {...configuration} />
            <JiraSettingsCredentials {...credentials} />
            <JiraSettingsStatus {...status} />
            <JiraSettingsCustomFields {...customFields} />
            <JiraSettingsHeadersTable {...headers} />
            <JiraSettingsComponents {...components} />
          </PaidFeatureGate>
        </>
      );
    }
  }

  return (
    <>
      {isLoading ? <Loader /> : body}
      <ModalAddJira
        show={shouldShowAddJiraModal}
        onHide={() => setShouldShowAddJiraModal(false)}
        isLoading={false}
        onAddJira={onAddSiteFn}
      />
    </>
  );
};

/**
 * Displays the global Jira settings page if the user has permission to view it.
 */
const JiraSettingsContainer: VoidFunctionComponent = () => (
  <OrganizationSettingsPermissionGate checkPermission={shouldDisplayJiraSettings}>
    <JiraSettings />
  </OrganizationSettingsPermissionGate>
);

export default JiraSettingsContainer;
